import { apm } from '@elastic/apm-rum';

import { TrackingProvider } from '../types/providers';
import { TrackingEvent } from '../types/tracking-event';
import { TrackingEventType } from '../types/events';
import { TrackingMetadata } from '../types/metadata';

export type ApmProviderConfig = Parameters<typeof apm.init>[0];

const APM_EVENT_BLACKLIST: TrackingEventType[] = ['ExampleBlacklisted'];

declare global {
  type ElasticConfig = {
    elasticApmServiceName: string;
    elasticApmServerUrl: string;
    elasticApmEnvironment: string;
    elasticApmServiceVersion: string;
  };

  interface Window {
    ELASTIC_CONFIG?: ElasticConfig;
  }
}

function isElasticConfigValid(maybeConfig: ElasticConfig | undefined): maybeConfig is ElasticConfig {
  return !!(maybeConfig && maybeConfig.elasticApmServiceName && maybeConfig.elasticApmServiceName.slice(0, 1) !== '$');
}

export class ApmProvider implements TrackingProvider {
  readonly name = 'apm';
  private initialized = false;

  constructor(private readonly config: ApmProviderConfig = {}) {
    this.config = config;
    this.config.active = true;
    this.config.instrument = true;
    this.config.distributedTracing = true;

    if (isElasticConfigValid(window.ELASTIC_CONFIG)) {
      this.config.serviceName = window.ELASTIC_CONFIG.elasticApmServiceName;
      this.config.serverUrl = window.ELASTIC_CONFIG.elasticApmServerUrl;
      this.config.environment = window.ELASTIC_CONFIG.elasticApmEnvironment;
      this.config.serviceVersion = window.ELASTIC_CONFIG.elasticApmServiceVersion;
    }
  }

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      apm.init(this.config);

      addEventListener('resize', function onResize() {
        apm.addLabels({
          'user_agent.device.width': window.innerWidth,
        });
      });

      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize APM provider:', error);
      throw error;
    }
  }

  async trackEvent(event: TrackingEvent, metadata: TrackingMetadata): Promise<void> {
    if (!this.initialized) {
      throw new Error('APM provider not initialized');
    }

    if (APM_EVENT_BLACKLIST.includes(event.type)) {
      return;
    }

    try {
      apm.setUserContext({
        id: metadata.userId,
      });
      if (event.type === 'Error') {
        apm.captureError(event.payload as Error, {
          labels: {
            sessionId: metadata.sessionId ?? '',
            ...Object.fromEntries(
              Object.entries(metadata.userProperties || {}).map(([key, value]) => [key, String(value)])
            ),
          },
        });
      } else {
        const transaction = apm.startTransaction(
          event.type,
          // This custom transaction type is used to select these transactions in APM for forwarding to
          // our database.
          'user-tracking',
          {
            startTime: performance.now() - 10,
          }
        );
        if (!transaction) {
          return;
        }
        // Add event data as custom context
        transaction.addLabels({
          // long-term, per-event data will be accessed by json traversal in SQL.
          payload: JSON.stringify(event.payload),
          // short-term, we will flatten this into labels for Elastic.
          ...Object.fromEntries(Object.entries(event.payload || {}).map(([key, value]) => [key, value])),
          sessionId: String(metadata.sessionId),
          priority: String(event.priority),
          // `userProperties` is for backwards compatibility but prefer to put your per-event data in the `payload`.
          ...Object.fromEntries(
            Object.entries(metadata.userProperties || {}).map(([key, value]) => [key, String(value)])
          ),
        });

        transaction.end();
      }
    } catch (error: unknown) {
      console.error('Failed to track event with APM:', String(error));
    }
  }

  destroy(): void {
    this.initialized = false;
  }
}
